import { useMemo } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useMutationEditAlias } from 'api/mutations/editAlias.mutation';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/SubProgramPreferences/AliasPreferences/EditAlias/editAlias.module.scss';
import { IAliasForm } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/SubProgramPreferences/AliasPreferences/alias.utils.type';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Select } from 'components/DesignSystem/Select';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';
import { CustomizedFormErrorMessage, CustomizedModalBase } from 'components/common';

interface Data {
    alias: string;
    contractId: number;
    id: number;
    scope: string;
    typeName: string;
}
interface Props {
    showModal: boolean;
    onHideModal: () => void;
    data: Data;
    tableData: Data[];
}

export const EditAlias = ({ showModal, onHideModal, data, tableData }: Props) => {
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
        setValue,
    } = useForm<IAliasForm>();

    const activityCode = useMemo<ISelectOption<string>>(
        () => ({
            value: data.typeName,
            label: `${data.scope}: ${data.typeName}`,
        }),
        [data],
    );

    const { mutate, isLoading } = useMutationEditAlias();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();

    const handleSave = (value) => {
        mutate(
            {
                alias: value.alias.trim(),
                id: data?.id,
            },
            {
                onSuccess: () => {
                    handleSnackBar('Alias has Updated', 'success');
                    onHideModal();
                    reset();
                },
                onError: (error) => DEFAULT_ERROR({ error, handleError: handleSnackBar }),
                onSettled: () => {
                    return queryClient.invalidateQueries('useQueryGetActivityCodesAliases');
                },
            },
        );
    };

    const handleHideModal = () => {
        reset();
        onHideModal();
    };

    const isAliasExist = (value) => {
        const isDuplicate = tableData.find((item) => {
            return item.alias === value;
        });

        return isDuplicate ? 'Alias name already exist' : true;
    };

    return (
        <Modal
            open={showModal}
            onClose={onHideModal}
            title={'Edit Alias'}
            subtitle={'Assign an alias to an activity code type:'}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={handleHideModal}>
                        Cancel
                    </Button>

                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        isLoading={isLoading}
                        disabled={isLoading}
                        onClick={handleSubmit(handleSave)}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <div className={classes.editAliasContainer}>
                <form onSubmit={handleSubmit(handleSave)}>
                    <div className={classes.content}>
                        <div className={classes.selectContainer}>
                            <div className={classes.selectTitle}>Activity Code Type:</div>
                            <div className={classes.selectWrapper}>
                                <Select<ISelectOption<string>>
                                    options={[]}
                                    id={'activity-code'}
                                    value={activityCode}
                                    isDisabled={true}
                                />
                            </div>
                        </div>

                        <div className={classes.selectContainer}>
                            <div className={classes.selectTitle}>Alias:</div>
                            <div className={classes.selectWrapper}>
                                <Controller
                                    render={({ field }) => (
                                        <TextInput {...field} placeholder={''} onClear={() => setValue('alias', '')} />
                                    )}
                                    defaultValue={data?.alias}
                                    name={'alias'}
                                    control={control}
                                    rules={{
                                        required: 'Alias is Required',
                                        minLength: { value: 2, message: 'Alias is to short' },
                                        validate: {
                                            isDuplicate: isAliasExist,
                                        },
                                    }}
                                />
                                {errors.alias && <CustomizedFormErrorMessage text={errors.alias.message} />}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Modal>
    );
};
